// Engine sound system with dynamic pitch shifting based on speed
// Uses Web Audio API directly via SFXManager for unified audio context

export class EngineSound {
    constructor(sfxManager) {
        this.sfxManager = sfxManager;
        this.source = null;
        this.gainNode = null;
        this.buffer = null;
        this.basePlaybackRate = 1.0;
        this.maxVolume = 0.5;
        this.soundPath = './assets/sfx/engine.mp3';
        this.loaded = false;
        this.isPlaying = false;
    }

    async load() {
        if (this.buffer) return;

        try {
            const response = await fetch(this.soundPath, { cache: 'reload' });
            if (!response.ok && response.status !== 206) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const arrayBuffer = await response.arrayBuffer();

            // Wait for SFXManager to have a context
            if (!this.sfxManager.audioContext) return;

            this.buffer = await this.sfxManager.audioContext.decodeAudioData(arrayBuffer);
            this.loaded = true;
        } catch (error) {
            // Silently fail - engine sound is non-critical
        }
    }

    start() {
        if (!this.buffer || !this.sfxManager.audioContext || !this.sfxManager.masterGain) {
            return;
        }

        // Don't start if already playing
        if (this.isPlaying) return;

        // Don't start if context is suspended
        if (this.sfxManager.audioContext.state === 'suspended') {
            return;
        }

        try {
            // Create gain node for volume control
            this.gainNode = this.sfxManager.audioContext.createGain();
            this.gainNode.gain.value = 0; // Start silent
            this.gainNode.connect(this.sfxManager.masterGain);

            // Create source node
            this.source = this.sfxManager.audioContext.createBufferSource();
            this.source.buffer = this.buffer;
            this.source.loop = true;
            this.source.playbackRate.value = this.basePlaybackRate;
            this.source.connect(this.gainNode);

            // Handle source ending (shouldn't happen with loop, but just in case)
            this.source.onended = () => {
                this.isPlaying = false;
            };

            this.source.start(0);
            this.isPlaying = true;
        } catch (e) {
            // Silently fail
        }
    }

    stop() {
        if (this.source && this.isPlaying) {
            try {
                this.source.stop();
            } catch (e) {
                // Already stopped
            }
        }
        this.isPlaying = false;
        this.source = null;
    }

    update(speed) {
        if (!this.gainNode || !this.source) return;

        // Try to start if not playing and context is now running
        if (!this.isPlaying && this.sfxManager.audioContext?.state === 'running') {
            this.start();
            return;
        }

        // Volume: fade from 10% to 100% based on speed
        const minVolume = 0.1 * this.maxVolume;
        let targetVolume = minVolume;

        if (speed > 3) {
            targetVolume = this.maxVolume;
        } else if (speed > 0.5) {
            const fadeRatio = (speed - 0.5) / 2.5;
            targetVolume = minVolume + (this.maxVolume - minVolume) * fadeRatio;
        }

        // Pitch: map speed 0-30 to playback rate 0.7-1.5
        const speedRatio = Math.min(Math.max(speed, 0) / 30, 1.0);
        const targetPlaybackRate = 0.7 + (speedRatio * 0.8);

        this.gainNode.gain.value = targetVolume;
        this.source.playbackRate.value = targetPlaybackRate;
    }

    setVolume(volume) {
        this.maxVolume = Math.max(0, Math.min(1, volume));
    }

    dispose() {
        this.stop();
        this.buffer = null;
        this.gainNode = null;
    }
}
